<template>
  <a-drawer
      v-model:visible="visible"
      :title="`字典数据管理 - ${dictType.typeName || ''}`"
      width="80%"
      :mask-closable="false"
      placement="right"
      ok-text="关闭"
      :hide-cancel="true"
  >
    <div class="dict-data-drawer-content">
      <!-- 字典数据筛选条件 -->
      <canvas-glass-card title="筛选条件" variant="light" :hoverable="false" class="filter-card">
        <a-form :model="queryForm" label-align="left">
          <a-grid :cols="4" :colGap="26" :rowGap="0">
            <a-grid-item>
              <a-form-item label="数据标签" label-col-flex="60px">
                <a-input v-model="queryForm.dictLabel" placeholder="请输入数据标签" allow-clear/>
              </a-form-item>
            </a-grid-item>
            <a-grid-item>
              <a-form-item label="数据值" label-col-flex="60px">
                <a-input v-model="queryForm.dictValue" placeholder="请输入数据值" allow-clear/>
              </a-form-item>
            </a-grid-item>
            <a-grid-item>
              <a-form-item label="状态" label-col-flex="60px">
                <a-select v-model="queryForm.status" placeholder="请选择状态">
                  <a-option value="">全部</a-option>
                  <a-option :value="true">启用</a-option>
                  <a-option :value="false">停用</a-option>
                </a-select>
              </a-form-item>
            </a-grid-item>
            <a-grid-item>
              <a-space>
                <a-button type="primary" @click="handleQuery" :loading="loading">
                  <template #icon>
                    <icon-search style="stroke: #fff"/>
                  </template>
                  搜索
                </a-button>
                <a-button type="primary" status="warning" @click="handleReset">重置</a-button>
                <a-button type="primary" @click="addDictData()">
                  <template #icon>
                    <icon-plus style="stroke: #fff"/>
                  </template>
                  新增数据
                </a-button>
              </a-space>
            </a-grid-item>
          </a-grid>
        </a-form>
      </canvas-glass-card>

      <a-space/>

      <!-- 字典数据表格 -->
      <canvas-glass-card variant="primary" :hoverable="false" class="table-card">
        <a-table
            row-key="id"
            :columns="tableColumns"
            :data="pagination.records"
            :pagination="false"
            :loading="loading"
            column-resizable
            stripe
            :bordered="{cell:true}"
        >
          <template #status="{ record }">
            <a-tag :color="record.status ? 'green' : 'red'">
              {{ record.status ? '启用' : '停用' }}
            </a-tag>
          </template>
          
          <template #isDefault="{ record }">
            <a-tag :color="record.isDefault ? 'green' : 'red'">
              {{ record.isDefault ? '是' : '否' }}
            </a-tag>
          </template>

          <template #createTime="{ record }">
            {{ formatDate(record.createTime) }}
          </template>

          <template #updateTime="{ record }">
            {{ formatDate(record.updateTime) }}
          </template>

          <template #action="{ record }">
            <a-space>
              <a-button type="text" size="mini" status="success" @click="editDictData(record)"
                        class="button-table-option">
                <template #icon>
                  <icon-edit />
                </template>
                编辑
              </a-button>
              <a-button type="text" size="mini" status="warning" @click="deleteDictDataBefore(record)"
                        class="button-table-option">
                <template #icon>
                  <icon-delete />
                </template>
                删除
              </a-button>
            </a-space>
          </template>
        </a-table>
        <!-- 字典数据分页 -->
        <a-pagination
            style="justify-content: end;margin: 10px;"
            v-model:current="pagination.current"
            v-model:page-size="pagination.size"
            :total="pagination.total"
            :page-size-options="[10, 20, 50, 100]"
            show-total
            show-jumper
            show-page-size
            @change="handlePageChange"
            @page-size-change="handlePageSizeChange"
        />
      </canvas-glass-card>
    </div>

    <!-- 新增/修改字典数据模态框 -->
    <dict-data-edit-modal
        v-model="isShowEditModal"
        :dict-data-id="currentEditDictDataId"
        :dict-type-id="dictType.id"
        @success="handleEditSuccess"
    />
    
    <!-- 字典数据删除模态框 -->
    <a-modal
        v-model:visible="isShowDeleteModal"
        :on-before-ok="delDictData"
        ok-text="删除"
        @cancel="isShowDeleteModal = false"
        draggable
    >
      <template #title>
        是否删除
      </template>
      <div>确定要删除[<strong>{{ dictDataInfoCopy.dictLabel }}</strong>]吗？</div>
    </a-modal>
  </a-drawer>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { getSysDictDataPage, deleteSysDictData } from '@/apis/system-data'
import CanvasGlassCard from '@/components/CanvasGlassCard.vue'
import DictDataEditModal from '@/components/System/Dictionary/DictDataEdit.vue'
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconDelete
} from '@arco-design/web-vue/es/icon'
import { Message } from "@arco-design/web-vue"
import {timestampNumber2dateString} from "@/utils/date-util";

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  dictType: {
    type: Object,
    default: () => ({})
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue'])

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const isShowEditModal = ref(false)
const isShowDeleteModal = ref(false)
const currentEditDictDataId = ref(null)
const dictDataInfoCopy = ref({})

// 筛选条件
const queryForm = ref({
  dictLabel: '',
  dictValue: '',
  status: null
})

// 字典数据列表数据
let pagination = ref({
  records: [],
  current: 1,
  size: 10,
  pages: 0,
  total: 0
})

// 字典数据表格列定义
const tableColumns = ref([
  // {title: 'ID', dataIndex: 'id', width: 80},
  {title: '排序', dataIndex: 'sort', width: 100, 
    sortable: {
      sortDirections: ['ascend', 'descend']
    },
  },
  {title: '数据标签', dataIndex: 'dictLabel', width: 180, ellipsis: true, tooltip: true},
  {title: '数据值', dataIndex: 'dictValue', width: 180, ellipsis: true, tooltip: true},
  {title: '状态', dataIndex: 'status', width: 80, slotName: 'status'},
  {title: '是否默认', dataIndex: 'isDefault', width: 100, slotName: 'isDefault'},
  {title: '备注', dataIndex: 'remark', ellipsis: true, tooltip: true},
  {title: '创建时间', dataIndex: 'createTime', width: 160, slotName: 'createTime'},
  {title: '更新时间', dataIndex: 'updateTime', width: 160, slotName: 'updateTime'},
  {title: '操作', dataIndex: 'action', width: 150, slotName: 'action', align: 'center', fixed: 'right'}
])

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.dictType.id) {
    handleReset()
    fetchDictDataList()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 获取字典数据列表
const fetchDictDataList = async () => {
  if (!props.dictType.id) {
    Message.warning('请先选择字典类型')
    return
  }
  
  loading.value = true

  getSysDictDataPage({
    typeId: props.dictType.id,
    ...queryForm.value,
    current: pagination.value.current,
    size: pagination.value.size
  }).then(response => {
    loading.value = false
    if (response.code === 0) {
      pagination.value = response.data
    } else {
      Message.error(response.message)
      console.error('获取字典数据列表失败:', response)
    }
  }).catch(error => {
    loading.value = false
    Message.error(error.message || '获取字典数据列表失败')
    console.error('获取字典数据列表失败:', error)
  })
}

// 搜索字典数据
const handleQuery = () => {
  pagination.current = 1
  fetchDictDataList()
}

// 重置筛选条件
const handleReset = () => {
  pagination.value.current = 1
  Object.keys(queryForm.value).forEach(key => {
    queryForm.value[key] instanceof Array ? queryForm.value[key].length = 0 : queryForm.value[key] = ''
  })
  fetchDictDataList()
}

// 处理页码变化
const handlePageChange = (page) => {
  fetchDictDataList()
}

// 处理每页数量变化
const handlePageSizeChange = (size) => {
  pagination.current = 1
  fetchDictDataList()
}

// 新增字典数据
const addDictData = () => {
  currentEditDictDataId.value = null
  isShowEditModal.value = true
}

// 编辑字典数据
const editDictData = (dictData) => {
  currentEditDictDataId.value = dictData.id
  isShowEditModal.value = true
}

// 编辑成功回调
const handleEditSuccess = () => {
  fetchDictDataList()
}

// 删除字典数据前操作
const deleteDictDataBefore = (dictData) => {
  dictDataInfoCopy.value = dictData
  isShowDeleteModal.value = true
}

// 删除字典数据
const delDictData = async () => {
  try {
    const response = await deleteSysDictData(dictDataInfoCopy.value.id)
    if (response.code === 0) {
      Message.success('删除成功')
      fetchDictDataList()
      return true
    } else {
      Message.error(response.message || '删除失败')
      return false
    }
  } catch (error) {
    Message.error(error.message || '删除失败')
    console.error('删除字典数据失败:', error)
    return false
  }
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '—'
  // return dateString.substring(0, 16).replace('T', ' ');
  return timestampNumber2dateString(dateString)
}

// 暴露给父组件的方法
defineExpose({
  refreshData: fetchDictDataList
})
</script>

<style scoped>
.dict-data-drawer-content {
  padding: 0;
}

.filter-card {
  margin-bottom: 16px;
}

.table-card {
  margin-bottom: 16px;
}

.button-table-option {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.button-table-option:hover {
  background-color: #f0f9ff;
}
</style>